import { useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '../app/hooks'
import {
  selectAuthUser,
  selectIsAuthenticated,
  selectAuthLoading,
  selectAuthError,
  selectAuthUserRole
} from '../features/auth/authSelectors'
import {
  selectUser,
  selectUsersLoading,
  selectUsersError
} from '../features/users/usersSelectors'
import {
  loginUser,
  logoutUser,
  resetPassword,
  signInWithGoogle,
  signInAttendantWithGoogle,
  registerWithGoogle,
  registerUser
} from '../features/auth/authThunks'
import { getUserByDocId } from '../features/users/usersThunks'
import { clearError } from '../features/auth/authSlice'
import { clearError as clearUserError, setUser } from '../features/users/usersSlice'
import { roleMap } from '@/utils/permissions'

export const useAuth = () => {
  const dispatch = useAppDispatch()

  const authUser = useAppSelector(selectAuthUser)
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const authLoading = useAppSelector(selectAuthLoading)
  const authError = useAppSelector(selectAuthError)

  const currentUser = useAppSelector(selectUser)
  const userRole = useAppSelector(selectAuthUserRole)
  const userLoading = useAppSelector(selectUsersLoading)
  const userError = useAppSelector(selectUsersError)

  const isLoading = authLoading || userLoading
  const error = authError || userError

  const login = useCallback(
    async (email: string, password: string) => {
      const authUserResult = await dispatch(loginUser({ email, password })).unwrap()
      const role = authUserResult.role ? roleMap[authUserResult.role.toLowerCase()] : null
       
      if (authUserResult?.uid && role) {
        const user = await dispatch(getUserByDocId({ docId: authUserResult.uid, role: role })).unwrap()
         
        dispatch(setUser(user))
      }

      return authUserResult
    },
    [dispatch]
  )

  const loginWithGoogle = useCallback(
    async () => {
      const authUserResult = await dispatch(signInWithGoogle()).unwrap()
      const role = authUserResult.role ? roleMap[authUserResult.role.toLowerCase()] : null

       
      if (authUserResult?.uid && role) {
        const user = await dispatch(getUserByDocId({ docId: authUserResult.uid, role: role })).unwrap()
        dispatch(setUser(user))
      }

      return authUserResult
    },
    [dispatch]
  )

  const loginAttendantWithGoogle = useCallback(
    async () => {
      const authUserResult = await dispatch(signInAttendantWithGoogle()).unwrap()
      const role = authUserResult.role ? roleMap[authUserResult.role.toLowerCase()] : null

      if (authUserResult?.uid && role) {
        const user = await dispatch(getUserByDocId({ docId: authUserResult.uid, role: role })).unwrap()
        dispatch(setUser(user))
      }

      return authUserResult
    },
    [dispatch]
  )

  const logout = useCallback(
    async () => {
      dispatch(setUser(null));
      return await dispatch(logoutUser()).unwrap()
    },
    [dispatch]
  )

  const resetPasswordRequest = useCallback(
    async (email: string) => {
      return await dispatch(resetPassword(email)).unwrap()
    },
    [dispatch]
  )

  const register = useCallback(
    async (email: string, password: string, userData: any) => {
      const authUserResult = await dispatch(registerUser({ email, password, userData })).unwrap()
      const role = authUserResult.role ? roleMap[authUserResult.role.toLowerCase()] : null

      if (authUserResult?.uid && role) {
        const user = await dispatch(getUserByDocId({ docId: authUserResult.uid, role: role })).unwrap()
        dispatch(setUser(user))
      }

      return authUserResult
    },
    [dispatch]
  )

  const registerGoogleAccount = useCallback(
    async (userData: any) => {
      const authUserResult = await dispatch(registerWithGoogle({ userData })).unwrap()
      const role = authUserResult.role ? roleMap[authUserResult.role.toLowerCase()] : null

       
      if (authUserResult?.uid && role) {
        const user = await dispatch(getUserByDocId({ docId: authUserResult.uid, role: role })).unwrap()
        dispatch(setUser(user))
      }
      return authUserResult
    },
    [dispatch]
  )

  const clearAuthError = useCallback(
    () => {
      dispatch(clearError())
      dispatch(clearUserError())
    },
    [dispatch]
  )

  return {
    authUser,
    isAuthenticated,
    currentUser,
    userRole,
    isLoading,
    error,
    authLoading,
    // Actions
    login,
    loginWithGoogle,
    loginAttendantWithGoogle,
    register,
    registerGoogleAccount,
    logout,
    resetPasswordRequest,
    clearAuthError,
  }
}
